import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { ApiClientError, tokenStore, type AuthClaims } from '@/lib/http-core';
import { Button, Input, Label } from '@/components/ui';
import type { AdminLoginResponse } from '@/lib/api-admin';
import { apiClient } from '@/lib/apiClient';
import { parseJwtRoles } from '@/lib/rbacGuard';
import { loginSchema, type LoginFormValues } from '../schemas/loginSchema';

const buildClaimsFromToken = (token: string): AuthClaims | null => {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const payload = parts[1] ?? '';
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
    const json =
      typeof atob === 'function'
        ? atob(padded + pad)
        : Buffer.from(padded + pad, 'base64').toString('binary');
    const decoded = JSON.parse(json) as {
      sub?: string;
      roles?: string[];
      twoFactorRequired?: boolean;
      exp?: number;
    };
    return {
      sub: decoded.sub ?? '',
      roles: decoded.roles ?? [],
      twoFactorRequired: decoded.twoFactorRequired === true,
      exp: decoded.exp ?? 0,
    } satisfies AuthClaims;
  } catch {
    return null;
  }
};

export const LoginPage = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [accountLocked, setAccountLocked] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { loginId: '', password: '', rememberMe: false },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    setAccountLocked(false);
    try {
      const [data] = await apiClient.request('post', '/admin/v1/auth/login', {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        body: { loginId: values.loginId, password: values.password },
      });
      const login = data as AdminLoginResponse;
      const claims = buildClaimsFromToken(login.accessToken);
      tokenStore.set(login.accessToken, claims);

      if (login.twoFactorRequired) {
        const challenge = encodeURIComponent(login.accessToken);
        const enroll = login.twoFactorEnrollmentRequired === true ? '&enroll=1' : '';
        // be-user H-2/H-3: loginId(백업코드 복구 식별) + twoFactorMethod(TOTP/PHONE 분기) 전달.
        const loginIdParam = `&loginId=${encodeURIComponent(values.loginId)}`;
        const method = (login.admin as { twoFactorMethod?: string }).twoFactorMethod;
        const methodParam = method ? `&method=${encodeURIComponent(method)}` : '';
        navigate(`/login/otp?challenge=${challenge}${enroll}${loginIdParam}${methodParam}`);
        return;
      }
      const roles = parseJwtRoles(login.accessToken)?.roles ?? [];
      const fallback = roles.length > 0 ? '/dashboard' : '/dashboard';
      navigate(fallback);
    } catch (error: unknown) {
      if (error instanceof ApiClientError) {
        if (error.code === 'USER_ACCOUNT_LOCKED') {
          setAccountLocked(true);
          return;
        }
        if (error.code === 'USER_CREDENTIALS_INVALID') {
          setSubmitError(t('errors.credentialsInvalid'));
          return;
        }
        setSubmitError(t(`errors.${error.code}`, { defaultValue: error.message }));
        return;
      }
      setSubmitError(t('errors.network'));
    }
  });

  return (
    <main
      className="grid min-h-dvh place-items-center px-6"
      style={{ background: 'var(--surface-1)' }}
    >
      <section
        className="w-full max-w-[400px] rounded-lg p-8"
        style={{
          background: 'var(--surface-raised)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <header className="mb-6">
          <h1
            className="text-2xl font-bold text-[color:var(--content-strong)]"
            style={{ fontFamily: 'var(--font-sans)' }}
          >
            {t('login.title')}
          </h1>
          <p className="mt-1 text-sm text-[color:var(--content-muted)]">
            {t('login.subtitle')}
          </p>
        </header>

        <form className="space-y-4" onSubmit={onSubmit} noValidate data-testid="admin-login-form">
          <div className="space-y-1.5">
            <Label htmlFor="loginId" required>{t('login.loginIdLabel')}</Label>
            <Input
              id="loginId"
              autoComplete="username"
              data-testid="admin-login-id"
              style={{ height: 'var(--density-compact-form-h)' }}
              {...form.register('loginId')}
              aria-invalid={form.formState.errors.loginId ? true : undefined}
            />
            {form.formState.errors.loginId ? (
              <p className="text-xs" style={{ color: 'var(--signal-danger)' }} role="alert">
                {t(form.formState.errors.loginId.message ?? 'errors.unknown')}
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password" required>{t('login.passwordLabel')}</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              data-testid="admin-login-password"
              style={{ height: 'var(--density-compact-form-h)' }}
              {...form.register('password')}
              aria-invalid={form.formState.errors.password ? true : undefined}
            />
            {form.formState.errors.password ? (
              <p className="text-xs" style={{ color: 'var(--signal-danger)' }} role="alert">
                {t(form.formState.errors.password.message ?? 'errors.unknown')}
              </p>
            ) : null}
          </div>

          <label className="flex items-center gap-2 text-sm text-[color:var(--content-default)]">
            <input type="checkbox" {...form.register('rememberMe')} />
            {t('login.rememberMe')}
          </label>

          {submitError ? (
            <div
              className="rounded-md p-3 text-sm"
              role="alert"
              style={{
                background: 'var(--status-warn-bg)',
                color: 'var(--status-warn-fg)',
              }}
            >
              {submitError}
            </div>
          ) : null}

          {accountLocked ? (
            <div
              className="rounded-md p-3 text-sm"
              role="alert"
              style={{
                background: 'var(--status-pending-bg)',
                color: 'var(--status-pending-fg)',
              }}
            >
              {t('errors.accountLocked')}
            </div>
          ) : null}

          <Button
            type="submit"
            variant="cta"
            className="w-full"
            style={{ height: '44px' }}
            disabled={form.formState.isSubmitting}
            data-testid="admin-login-submit"
          >
            {form.formState.isSubmitting ? t('login.submitting') : t('login.submit')}
          </Button>

          <p className="text-center text-xs text-[color:var(--content-muted)]">
            {t('login.forgotPasswordHelp')}
          </p>
        </form>
      </section>
    </main>
  );
};
